import { z } from "zod";
import { OpportunityStatusSchema } from "./opportunities";
import type { OpportunityStatus } from "./opportunities";

/** Day name labels indexed by JS getDay() (0 = Sunday … 6 = Saturday). */
export const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

/**
 * Day of week (0 = Sunday … 6 = Saturday) the Agent Pulse cron generates
 * its weekly memo. Every time-alignment in the cron, backend, and frontend
 * should reference this constant so the schedule has a single source of truth.
 *
 * The memo reports the seven days ending the day before it runs, so this also
 * fixes the reporting week: generating on Friday means a Friday-to-Thursday
 * week, closed the night before, so the window is always complete before the
 * memo queries it.
 */
export const AGENT_PULSE_GENERATION_DAY = 5; // Friday

/**
 * Local hour of day (0–23) the Agent Pulse memo should be generated for each
 * organization, evaluated in that organization's own timezone. The cron runs
 * hourly and only generates a memo when the local time in an org's timezone
 * matches both AGENT_PULSE_GENERATION_DAY and this hour. The memo is emailed
 * inline by the same run, so this is also when recipients receive it.
 */
export const AGENT_PULSE_GENERATION_HOUR = 7; // 7am local time

/** Lightweight opportunity data for cross-module reporting. */
export interface OpportunityActivitySummary {
  id: string;
  name: string;
  status: OpportunityStatus;
  amount: string;
  companyName: string | null;
  createdAt: Date;
  closedAt: Date | null;
}

/** Lightweight quote header for cross-module reporting. */
export interface QuoteSummary {
  id: string;
  quoteNumber: number;
  name: string | null;
  status: string;
  companyName: string | null;
  createdAt: Date;
}

/** Lightweight quote line item for cross-module reporting. */
export interface QuoteLineSummary {
  quoteId: string;
  description: string;
  type: string;
}

/** Thread message data for cross-module reporting. */
export interface ThreadMessageSummary {
  threadId: string;
  threadName: string | null;
  messageId: string;
  message: unknown;
  userId: string | null;
  userName: string;
  createdAt: Date;
}

/** User message count for leaderboard reporting. */
export interface UserMessageCount {
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  messageCount: number;
}

/** Metadata stored alongside each weekly Agent Pulse update. */
export interface AgentPulseUpdateMetadata {
  modelUsed?: string;
  /** Day name (e.g. "Thursday") when the memo was generated. */
  generationDay?: string;
  dataSnapshot?: {
    opportunityCount: number;
    quoteCount: number;
    activityEventCount?: number;
    /** User ID of the top message sender for the week. */
    topMessageUserId?: string | null;
    /** Message count of the top sender for the week. */
    topMessageCount?: number | null;
    /** Total quote views across the summary period at pulse time. */
    quoteViewsTotal?: number;
    /** Quote views in the most recent week at pulse time. */
    quoteViewsThisWeek?: number;
    /** Number of opportunities with ACCEPTED status during the pulse period. */
    wonOpportunitiesCount?: number;
    /** Total dollar amount of won opportunities during the pulse period. */
    wonOpportunitiesAmount?: number;
    /** Number of opportunities with LOST status during the pulse period. */
    lostOpportunitiesCount?: number;
    /** Total dollar amount of lost opportunities during the pulse period. */
    lostOpportunitiesAmount?: number;
  };
}

/** Raw prompt sent to the model when generating a weekly Agent Pulse memo. */
export interface AgentPulseRawDataSnapshot {
  system: string;
  user: string;
}

/** Schema for the snapshot counts surfaced from update metadata. */
export const AgentPulseDataSnapshotSchema = z.object({
  opportunityCount: z.number(),
  quoteCount: z.number(),
  activityEventCount: z.number().optional(),
  topMessageUserId: z.string().nullable().optional(),
  topMessageUserName: z.string().nullable().optional(),
  topMessageCount: z.number().nullable().optional(),
  quoteViewsTotal: z.number().optional(),
  quoteViewsThisWeek: z.number().optional(),
  wonOpportunitiesCount: z.number().optional(),
  wonOpportunitiesAmount: z.number().optional(),
  lostOpportunitiesCount: z.number().optional(),
  lostOpportunitiesAmount: z.number().optional(),
});
export type AgentPulseDataSnapshot = z.infer<
  typeof AgentPulseDataSnapshotSchema
>;

/** Schema for a single Agent Pulse weekly update. */
export const AgentPulseUpdateSchema = z.object({
  id: z.string(),
  content: z.string(),
  weekStartDate: z.coerce.date(),
  weekEndDate: z.coerce.date(),
  createdAt: z.coerce.date(),
  dataSnapshot: AgentPulseDataSnapshotSchema.nullable().optional(),
});
export type AgentPulseUpdate = z.infer<typeof AgentPulseUpdateSchema>;

/** Response schema for listing historical updates. */
export const GetAgentPulseUpdatesResponseSchema = z.object({
  updates: z.array(AgentPulseUpdateSchema),
});
export type GetAgentPulseUpdatesResponse = z.infer<
  typeof GetAgentPulseUpdatesResponseSchema
>;

/** Response schema for the latest update. */
export const GetLatestAgentPulseUpdateResponseSchema = z.object({
  update: AgentPulseUpdateSchema.nullable(),
});
export type GetLatestAgentPulseUpdateResponse = z.infer<
  typeof GetLatestAgentPulseUpdateResponseSchema
>;

/** Schema for a single leaderboard entry. */
export const AgentPulseLeaderboardEntrySchema = z.object({
  userId: z.string(),
  userName: z.string(),
  messageCount: z.number(),
});
export type AgentPulseLeaderboardEntry = z.infer<
  typeof AgentPulseLeaderboardEntrySchema
>;

/** Response schema for the leaderboard. */
export const GetAgentPulseLeaderboardResponseSchema = z.object({
  entries: z.array(AgentPulseLeaderboardEntrySchema),
});
export type GetAgentPulseLeaderboardResponse = z.infer<
  typeof GetAgentPulseLeaderboardResponseSchema
>;

/** Schema for a won or lost opportunity surfaced in the Agent Pulse dashboard. */
export const AgentPulseWonLostOpportunitySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: OpportunityStatusSchema.extract(["ACCEPTED", "LOST"]),
  amount: z.string().nullable(),
  companyName: z.string().nullable(),
  contactName: z.string().nullable(),
  salesRepName: z.string().nullable(),
  closedAt: z.coerce.date().nullable(),
});
export type AgentPulseWonLostOpportunity = z.infer<
  typeof AgentPulseWonLostOpportunitySchema
>;

/** Response schema for the Agent Pulse won/lost opportunities endpoint. */
export const GetAgentPulseWonLostOpportunitiesResponseSchema = z.object({
  opportunities: z.array(AgentPulseWonLostOpportunitySchema),
});
export type GetAgentPulseWonLostOpportunitiesResponse = z.infer<
  typeof GetAgentPulseWonLostOpportunitiesResponseSchema
>;

/** Response schema for live counts of opportunities and quotes since the last pulse. */
export const GetAgentPulseLiveCountsResponseSchema = z.object({
  opportunities: z.number(),
  quotes: z.number(),
});
export type GetAgentPulseLiveCountsResponse = z.infer<
  typeof GetAgentPulseLiveCountsResponseSchema
>;
